import {
  computed,
  IObservableArray,
  makeAutoObservable,
  observable,
} from "mobx";
import Fuse from "fuse.js";
import { ModuleRowDTO } from "../../ProfileWindow/AdminWindows/ModuleListWindow/DTO/ModuleRowDTO";
import { ModuleResourceRowDTO } from "../../ProfileWindow/AdminWindows/ModuleEditWindow/DTO/ModuleResourceRowDTO";
import {
  ModuleResponseApi,
  ModuleWithResourcesAndTasksResponseApi,
} from "../../DTO/ServerResponseType/Module/Module";
import {
  AllModuleCol,
  ModuleColEnum,
} from "../../ProfileWindow/AdminWindows/ModuleListWindow/Enums/ModuleColEnum";
import { SortPatternEnum } from "../../Enums/SortPatternEnum";
import ModuleServerClient from "../../ServerClients/ModuleServerClient";

const fuseOptions = {
  isCaseSensitive: false,
  shouldSort: false,
};

let fuseName: Fuse<ModuleRowDTO> = new Fuse([], {
  ...fuseOptions,
  keys: ["moduleName"],
});
let fuseResourceName: Fuse<ModuleResourceRowDTO> = new Fuse([], {
  ...fuseOptions,
  keys: ["name"],
});

export class ModulesStore {
  public modules: IObservableArray<ModuleRowDTO> = observable.array([]);
  public showDeleteModal: boolean = false;
  public deleteModuleData?: ModuleResponseApi;

  public searchPatterns: IObservableArray<string> = observable.array(
    new Array(AllModuleCol.length).fill("")
  );
  public sortPattern: IObservableArray<SortPatternEnum> = observable.array(
    new Array(AllModuleCol.length).fill(SortPatternEnum.None)
  );

  constructor() {
    makeAutoObservable(this);
  }

  public onDeleteModalOpen = (moduleData: ModuleRowDTO) => {
    this.showDeleteModal = true;
    this.deleteModuleData = moduleData;
  };

  public onDeleteModalClose = () => {
    this.showDeleteModal = false;
  };

  public setModules = (
    modules: ModuleWithResourcesAndTasksResponseApi[] & {
      isModuleOpen?: boolean;
    }
  ) => {
    this.searchPatterns.fill("");
    this.sortPattern.fill(SortPatternEnum.None);
    if (modules)
      this.modules.replace(
        Array.from(modules, (module, index) => {
          let moduleRow = new ModuleRowDTO(module);
          moduleRow.moduleOrder = index + 1;
          return moduleRow;
        })
      );
  };

  deleteModuleAsync = async (moduleId: string) => {
    return ModuleServerClient.sendToDeleteModuleAsync(moduleId).then(() => {
      this.modules.replace(
        this.modules.filter(
          (module: ModuleResponseApi) => module.moduleId !== moduleId
        )
      );
    });
  };

  public onChangeSearchPattern = (
    searchPattern: string,
    indSearchPattern: number
  ) => {
    this.searchPatterns[indSearchPattern] = searchPattern;
  };

  onChangeSortPattern(indSortPattern: number) {
    let currentSortPattern = this.sortPattern[indSortPattern];
    this.sortPattern.fill(SortPatternEnum.None);
    this.sortPattern[indSortPattern] =
      currentSortPattern === SortPatternEnum.None
        ? SortPatternEnum.SortAZ
        : currentSortPattern === SortPatternEnum.SortAZ
        ? SortPatternEnum.SortZA
        : SortPatternEnum.None;
  }

  @computed get sortedAndSearchedModules(): ModuleRowDTO[] {
    let newModules: ModuleRowDTO[] = JSON.parse(JSON.stringify(this.modules));

    // searched

    // for all searched col
    this.searchPatterns.forEach((searchedPattern, index) => {
      if (searchedPattern === "") return; // no searched pattern = no search
      switch (index) {
        case ModuleColEnum.Name: {
          fuseName.setCollection(newModules);
          newModules = fuseName.search(searchedPattern).map(({ item }) => item);
          break;
        }
        case ModuleColEnum.Scenario:
        case ModuleColEnum.Task:
        case ModuleColEnum.Video:
        case ModuleColEnum.MiniGame:
        case ModuleColEnum.Presentation:
        case ModuleColEnum.MaterialForStudent: {
          // Limit the search to only certain resources
          newModules = newModules.filter((module) => {
            let tempModuleResources = module.moduleResourcesByType[index];
            fuseResourceName.setCollection(tempModuleResources);
            tempModuleResources = fuseResourceName
              .search(searchedPattern)
              .map(({ item }) => item);
            return tempModuleResources.length !== 0;
          });
          break;
        }
      }
    });

    if (newModules.length === 0) return [];

    // sorted

    let moduleCol = Math.max(
      this.sortPattern.indexOf(SortPatternEnum.SortAZ),
      this.sortPattern.indexOf(SortPatternEnum.SortZA)
    );

    if (moduleCol !== -1)
      newModules.sort((el1, el2) => {
        let res = 0;
        if (moduleCol === ModuleColEnum.Name)
          res = el1.moduleName.localeCompare(el2.moduleName);
        else
          res = el1.moduleResourcesByType[moduleCol][0]?.name.localeCompare(
            el2.moduleResourcesByType[moduleCol][0]?.name
          );

        if (this.sortPattern[moduleCol] === SortPatternEnum.SortZA) res *= -1;

        return res;
      });

    return newModules ? newModules : [];
  }

  public loadAllModulesWithResourcesAndTasksAsync = async (): Promise<
    ModuleWithResourcesAndTasksResponseApi[] | never
  > => {
    return ModuleServerClient.sendToGetAllModulesWithResourcesAndTasksAsync().then(
      (modules) => {
        this.setModules(modules);
        return modules;
      }
    );
  };
}
